import React, { useState, useEffect, useRef } from "react";
import ReportParamsView from "./ReportParams/ReportParamsView";
import { prepareSaveContent } from "../save";
import api from "../../api/api";
import {
  handleSelectAll,
  handleOptionSelect,
  allUsesOptional,
  loadUdfs,
  refreshAllQueries,
  groupUDFsByCategory,
  handleUdfCategorySelect,
  handleUdfItemSelect,
  handleMasterUdfSelect,
  type ParameterDefinition,
  type Item,
  type UDFCategory,
  type ItemsState,
  type StringState,
} from "./ReportServices";
import { loadReportParamsDependentData, updateReportParamsCanGetDataState } from "./ReportParams/ReportParamsParameterLoader";
import { saveReportParams } from "./ReportParams/ReportParamsSaveParamsService";
import { loadReportData } from "./ReportParams/ReportParamsDataLoadService";
import { registerReportDrilldownSelectionHandler } from "./ReportParams/ReportParamsDrilldownService";

interface ReportParamsInterface {
  authToken: string;
  identityProvider: string;
  shouldRefresh?: Date;
}

const ReportParams: React.FC<ReportParamsInterface> = ({ authToken, identityProvider, shouldRefresh }) => {
  const pageSize = 1000;
  const longRunningDataLoadThresholdMs = 10000; // 10 seconds

  type BooleanState = Record<string, boolean>;

  const [items, setItemsState] = useState<ItemsState>({});
  const [udfs, setUdfs] = useState<Item[]>([]);
  const [udfCategories, setUdfCategories] = useState<UDFCategory[]>([]);
  const [selectedItem, setSelectedItemState] = useState<StringState>({});
  const [selectedUdfs, setSelectedUdfs] = useState<string>("");
  const [areItemsLoading, setAreItemsLoadingState] = useState<BooleanState>({});
  const [areUdfsLoading, setAreUdfsLoading] = useState<boolean>(false);
  const [canGetData, setCanGetData] = useState<boolean>(false);
  const [canGetDatas, setCanGetDatas] = useState<boolean[]>();
  const [gotData, setGotData] = useState<boolean>(true);
  const [reportName, setReportName] = useState<string>("");
  const [sourceTemplateName, setSourceTemplateName] = useState<string>("");
  const [overWrite, setOverWrite] = useState<string>("false");
  const [parameterDefinitions, setParameterDefinitions] = useState<ParameterDefinition[]>([]);
  const [dataDefinition, setDataDefinition] = useState<ParameterDefinition>({
    name: "",
    type: "",
    call: "",
    default: "",
  });
  const [dataDefinitions, setDataDefinitions] = useState<ParameterDefinition[]>([{ name: "", type: "", call: "", default: "" }]);
  const [selectedDataDefinitionIndex, setSelectedDataDefinitionIndexState] = useState<number>(0);
  const [comboboxQuery, setComboboxQuery] = useState<string>("");
  const [udfComboboxQuery, setUdfComboboxQuery] = useState<string>("");
  const [comboboxOpen, setComboboxOpen] = useState<string>("");
  const [isComboboxOpen, setIsComboboxOpen] = useState<boolean>(false);
  const [showLongRunningDataLoadNotice, setShowLongRunningDataLoadNotice] = useState<boolean>(false);
  const [isSaveConfirmationOpen, setIsSaveConfirmationOpen] = useState<boolean>(false);
  const [saveCompleted, setSaveCompleted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [canCancel, setCanCancel] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [percent, setPercent] = useState<number>(0);
  const [isRefreshQueriesMessageShown, setIsRefreshQueriesMessageShown] = useState<boolean>(false);
  const [dataLoadDuration, setDataLoadDuration] = useState<number>(0);
  const [enableDrilldownLinks, setEnableDrilldownLinks] = useState<boolean>(true);
  const dataLoadCancelTokenRef = useRef<string>("");
  const drilldownCancelTokenRef = useRef<string>("");
  const dataLoadDurationRef = useRef<number>(0);
  const dataLoadLongRunningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enableDrilldownLinksRef = useRef<boolean>(true);

  useEffect(() => {
    enableDrilldownLinksRef.current = enableDrilldownLinks;
  }, [enableDrilldownLinks]);

  useEffect(() => {
    return () => {
      if (dataLoadLongRunningTimerRef.current) {
        clearTimeout(dataLoadLongRunningTimerRef.current);
      }
    };
  }, []);

  const clearLongRunningDataLoadTimer = () => {
    if (dataLoadLongRunningTimerRef.current) {
      clearTimeout(dataLoadLongRunningTimerRef.current);
      dataLoadLongRunningTimerRef.current = null;
    }
  };

  const startLongRunningDataLoadNoticeTimer = () => {
    clearLongRunningDataLoadTimer();
    setShowLongRunningDataLoadNotice(false);
    dataLoadLongRunningTimerRef.current = setTimeout(() => {
      setShowLongRunningDataLoadNotice(true);
      dataLoadLongRunningTimerRef.current = null;
    }, longRunningDataLoadThresholdMs);
  };

  const stopLongRunningDataLoadNoticeTimer = () => {
    clearLongRunningDataLoadTimer();
    setShowLongRunningDataLoadNotice(false);
  };

  const setItems = (key: string, items: Item[]) => {
    setItemsState((prev) => ({ ...prev, [key]: items }));
  };
  const setAreItemsLoading = (key: string, value: boolean) => {
    setAreItemsLoadingState((prev) => ({ ...prev, [key]: value }));
  };

  const loadUdfsLocal = async (call: string) => {
    if (!call || !call.includes("&udf=*")) {
      setUdfs([]);
      setUdfCategories([]);
      return;
    }

    try {
      setAreUdfsLoading(true);
      const rowItems = await loadUdfs(call, authToken, identityProvider);
      setUdfs(rowItems);
      setUdfCategories(groupUDFsByCategory(rowItems));
    } catch (error) {
      setError("Error fetching UDFs: " + error);
    } finally {
      setAreUdfsLoading(false);
    }
  };

  const setSelectedItem = (key: string, item: string) => {
    let newSelectedItem = { ...selectedItem, [key]: item };
    setSelectedItemState(newSelectedItem);
    setTimeout(() => {
      Excel.run(async (context) => {
        await saveParams(context, newSelectedItem);
      });
      loadDependentData(parameterDefinitions, dataDefinitions, newSelectedItem, null, false, key);
    });
  };

  const setSelectedDataDefinitionIndex = (value: number, dataDefinitions: ParameterDefinition[]) => {
    setSelectedUdfs("");
    setSelectedDataDefinitionIndexState(value);
    setTimeout(async () => {
      await loadUdfsLocal(dataDefinitions[value]?.call ?? "");
    });
  };

  const loadDependentData = async (
    parameterDefinitions: ParameterDefinition[],
    dataDefinitions: ParameterDefinition[],
    selectedItem: StringState,
    newSelectedDataDefinitionIndex: number | null = null,
    initial = false,
    changedParameter?: string
  ) => {
    await loadReportParamsDependentData({
      parameterDefinitions,
      dataDefinitions,
      selectedItem,
      items,
      selectedDataDefinitionIndex,
      setItems,
      setAreItemsLoading,
      setCanGetDatas,
      setCanGetData,
      setError,
      authToken,
      identityProvider,
      newSelectedDataDefinitionIndex,
      initial,
      changedParameter,
    });
  };

  const loadDependentDefData = (
    parameterDefinitions: ParameterDefinition[],
    dataDefinitions: ParameterDefinition[],
    selectedItem: StringState,
    newSelectedDataDefinitionIndex: number | null = null) => {
      updateReportParamsCanGetDataState({
        parameterDefinitions,
        dataDefinitions,
        selectedItem,
        selectedDataDefinitionIndex,
        setCanGetDatas,
        setCanGetData,
        newSelectedDataDefinitionIndex,
      });
  };

  useEffect(() => {
    // Do not load without a usable token: the first mount can run with authToken
    // still empty (see the Okta path in LoginButton.oktaLoginCompleted).
    //
    // authToken/identityProvider are deliberately NOT in the dependency array:
    // this effect calls registerReportDrilldownSelectionHandler, which adds an
    // Excel onSelectionChanged handler, is not idempotent, and offers no way to
    // remove it. Re-running on every token change would register a duplicate
    // handler each time. Fixing that handler lifecycle is tracked separately.
    if (!authToken) return;

    var initial = true;

    const loadExcelConfiguration = async () => {
      const extractParam = (row: any, parameterValues: { name: string; value: string }[]) => ({
        name: row[0],
        type: row[1],
        call: row[2],
        default: row[3],
        value: parameterValues.find((p) => p.name == row[0])?.value,
      });

      await Excel.run(async (context) => {
        {
          const sampleName = context.workbook.names.getItemOrNullObject("sample");
          await context.sync();
          if (!sampleName.isNullObject) {
            setGotData(false);
          }
        }

        {
          api.setTemplateId("");
          await context.sync();
          {
            let templateName = "";
            let templateNameRange = context.workbook.names.getItemOrNullObject("template");
            templateNameRange.load("type");
            await context.sync();
            // getRange() is only legal on a name that resolves to a range. On a name defined as a
            // formula or a constant it throws InvalidOperation ("This operation is not permitted for
            // the current object"), which used to surface as a raw Office.js message in the task pane.
            if (!templateNameRange.isNullObject && templateNameRange.type === Excel.NamedItemType.range) {
              const range = templateNameRange.getRange();
              range.load("values");
              await context.sync();
              templateName = range.values[0][0] || "";
            }
            setSourceTemplateName(templateName);
            let templateId = "";
            try {
              context.workbook.properties.load("custom");
              await context.sync();
              const prop = context.workbook.properties.custom.getItemOrNullObject(
                "BuilderMetrix.TemplateId"
              );
              prop.load("key,value,type");
              await context.sync();
              templateId = prop.isNullObject ? "" : String(prop.value ?? "");
              api.setTemplateId(templateId);
            } catch (e) {
              // Custom property might not exist, continue without it
              console.log("Could not retrieve BuilderMetrix.TemplateId property:", e);
            }
            let reportNameRange = context.workbook.names.getItemOrNullObject("report");
            reportNameRange.load("type");
            await context.sync();
            if (!reportNameRange.isNullObject && reportNameRange.type === Excel.NamedItemType.range) {
              const range = reportNameRange.getRange();
              range.load("values");
              await context.sync();
              let name = range.values[0][0] || "";
              if (name.length > 0 && name != templateName) {
                setReportName(name);
              }
            }
          }
        }

        let dataDef = null;
        let udfDataDef = null;

        let parameterValues: { name: string; value: string }[] = [];
        if (initial) {
          let sheet = context.workbook.worksheets.getItemOrNullObject("(parameters)");
          await context.sync();
          if (!sheet.isNullObject) {
            let range = sheet.getUsedRange();
            range.load("values");
            await context.sync();
            let pv = range.values.slice(2).map((row: any) => ({ name: row[0], value: row[1] }));
            dataDef = pv.find((p) => p.name == "Data");
            if (dataDef)
              pv.splice(pv.indexOf(dataDef!), 1);
            udfDataDef = pv.find((p) => p.name == "UDFs");
            if (udfDataDef)
              pv.splice(pv.indexOf(udfDataDef!), 1);
            parameterValues = pv;
          }
        }

        {
          let sheet = context.workbook.worksheets.getItemOrNullObject("(configuration)");
          sheet.load("name");
          await context.sync();
          // Calling getUsedRange() on the null object throws InvalidOperation ("This operation is not
          // permitted for the current object"). The task pane can outlive the workbook it probed for
          // this sheet, so the sheet has to be re-checked here rather than assumed.
          if (sheet.isNullObject) {
            setError("This workbook has no BuilderMetrix configuration, so there is nothing to load. Open a BuilderMetrix analyzer or template workbook.");
            return;
          }
          let range = sheet.getUsedRange();
          range.load("values");
          await context.sync();
          let allParameterDefinitions = range.values.map((row) => extractParam(row, parameterValues));
          let dataDefinitions = [];
          // browse allParameterDefinitions in reverse order to find data definitions at the end - having type "(data)"
          for (let i = allParameterDefinitions.length - 1; i >= 0; i--) {
            let parameter = allParameterDefinitions[i];
            if (parameter.type == "(data)") {
              dataDefinitions.unshift(parameter);
            } else {
              break;
            }
          }
          let parameterDefinitions = allParameterDefinitions
            .slice(1, -dataDefinitions.length)
            .map((parameter) => ({
              ...parameter,
              required: !allUsesOptional(parameter.name, allParameterDefinitions),
            }));
          setParameterDefinitions(parameterDefinitions);
          let dataDefinition =
            dataDefinitions.length > 0
              ? dataDefinitions[0]
              : { name: "", type: "", call: "", default: "" };
          setDataDefinition(dataDefinition);
          setDataDefinitions(dataDefinitions);
          if (dataDef) {
            let i = dataDefinitions.findIndex((def) => def.name == (dataDef ? dataDef.value : ""));
            setSelectedDataDefinitionIndex(i >= 0 ? i : 0, dataDefinitions);
          }
          else {
            setSelectedDataDefinitionIndex(selectedDataDefinitionIndex, dataDefinitions);
          }
          if (udfDataDef) {
            setSelectedUdfs(udfDataDef.value || "");
          }
          await loadDependentData(parameterDefinitions, dataDefinitions, selectedItem, null, true);
        }

        await registerReportDrilldownSelectionHandler({
          enableDrilldownLinksRef,
          drilldownCancelTokenRef,
          selectedItem,
          authToken,
          identityProvider,
          pageSize,
        });
        initial = false;
      });
    };

    loadExcelConfiguration().catch((error) => {
      console.error("Error loading the template configuration:", error);
      setError("Error loading the template configuration: " + (error instanceof Error ? error.message : String(error)));
    });
  }, [shouldRefresh]);

  // Handle select all or deselect all. `targetItems` is the full item list normally, or the
  // currently filtered subset when the combobox has a search query active.
  const handleSelectAllLocal = (nameId: string, targetItems: Item[]) => {
    handleSelectAll(targetItems, selectedItem[nameId], (val) => setSelectedItem(nameId, val));
  };

  // Handle individual option selection
  const handleOptionSelectLocal = (nameId: string, data: any, targetItems: Item[]) => {
    handleOptionSelect(targetItems, selectedItem[nameId], (val) => setSelectedItem(nameId, val), data);
  };

  const handleUdfCategoryToggle = (categoryName: string, isSelected: boolean) => {
    const newSelectedUdfs = handleUdfCategorySelect(categoryName, isSelected, udfCategories, selectedUdfs);
    setSelectedUdfs(newSelectedUdfs);
  };

  const handleUdfItemToggle = (itemId: string, isSelected: boolean) => {
    const newSelectedUdfs = handleUdfItemSelect(itemId, isSelected, selectedUdfs);
    setSelectedUdfs(newSelectedUdfs);
  };

  const handleMasterUdfToggle = (isSelected: boolean) => {
    const newSelectedUdfs = handleMasterUdfSelect(isSelected, udfCategories);
    setSelectedUdfs(newSelectedUdfs);
  };

  const handleGetDataInternal = async (context: Excel.RequestContext, selectedDataDefinitionIndex: number) => {
    startLongRunningDataLoadNoticeTimer();
    try {
      await loadReportData(context, {
        selectedDataDefinitionIndex,
        parameterDefinitions,
        dataDefinition,
        dataDefinitions,
        selectedItem,
        items,
        selectedUdfs,
        udfs,
        authToken,
        identityProvider,
        pageSize,
        dataLoadCancelTokenRef,
        dataLoadDurationRef,
        saveParams,
        refreshAllQueriesLocal,
        setPercent,
        setIsLoading,
        setCanCancel,
        setDataLoadDuration,
        setGotData,
        setError,
        onLoadingComplete: stopLongRunningDataLoadNoticeTimer,
      });
    } finally {
      stopLongRunningDataLoadNoticeTimer();
    }
  };

  async function refreshAllQueriesLocal() {
    const shouldShowRefreshQueriesMessage = await refreshAllQueries();
    if (shouldShowRefreshQueriesMessage) {
      setIsRefreshQueriesMessageShown(true);
    }
  }

  const handleGetDataLocal = async () => { 
    await handleGetData(selectedDataDefinitionIndex);
  };

  const handleCancelDataLoad = () => {
    dataLoadCancelTokenRef.current = "";
    stopLongRunningDataLoadNoticeTimer();
    setCanCancel(false);
    setPercent(0);
    setIsLoading(false);
    console.log("User requested data load cancel");
  };
  
  const handleGetData = async (selectedDataDefinitionIndex: number) => {
    Excel.run(async (context) => {
      await handleGetDataInternal(context, selectedDataDefinitionIndex);

      return context.sync();
    });
  };

  const saveParams = async (context: Excel.RequestContext, selectedItem: StringState, saveHeader = false) => {
    await saveReportParams(context, {
      reportName,
      parameterDefinitions,
      dataDefinitions,
      selectedDataDefinitionIndex,
      selectedItem,
      selectedUdfs,
      items,
      udfs,
      dataLoadDuration: dataLoadDurationRef.current,
      saveHeader,
      setError,
    });
  };

  const handleSaveInternal = async () => {
    Excel.run(async (context) => {
      setIsLoading(true);

      await saveParams(context, selectedItem, true);
      try {
        let excelContentToSave = await prepareSaveContent();
        const templateName = sourceTemplateName || dataDefinition.default || "";

        const response = await api.post(
          `report/file?FileName=${encodeURIComponent(reportName)}&TemplateName=${encodeURIComponent(templateName)}&OverWrite=${encodeURIComponent(overWrite)}`,
          {
            fileContent: excelContentToSave,
            lastModified: new Date().toISOString(),
          },
          authToken
        );
        if (!response.ok) {
          throw new Error("Response not OK");
        }

        console.log("Save successful.");

        setSaveCompleted(true);
      } catch (error) {
        setError("Save failed: " + error);
      } finally {
        setIsLoading(false);
        setOverWrite("false");
      }
    });
  };

  const handleSaveConfirmationClick = async () => {
    await handleSaveInternal();
  };

  const handleSave = async () => {
    setIsLoading(true);
    const templateName = sourceTemplateName || dataDefinition.default || "";

    try {
      const response = await api.fetch(
        `report/file/exists?FileName=${encodeURIComponent(reportName)}.xlsx&TemplateName=${encodeURIComponent(templateName)}`,
        authToken,
        identityProvider
      );
      if (response.status == 404) {
        // File does not exist, proceeding to save.
        setOverWrite("false");
        await handleSaveInternal();
      } else if (response.ok) {
        setOverWrite("true");
        setIsSaveConfirmationOpen(true);
      } else {
        throw new Error("Response not OK");
      }
    } catch (error) {
      setError("Error checking report existence: " + error);
      return;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ReportParamsView
      reportName={reportName}
      sourceTemplateName={sourceTemplateName}
      dataDefinition={dataDefinition}
      parameterDefinitions={parameterDefinitions}
      dataDefinitions={dataDefinitions}
      selectedDataDefinitionIndex={selectedDataDefinitionIndex}
      items={items}
      udfs={udfs}
      udfCategories={udfCategories}
      selectedItem={selectedItem}
      selectedUdfs={selectedUdfs}
      areItemsLoading={areItemsLoading}
      areUdfsLoading={areUdfsLoading}
      canGetData={canGetData}
      canGetDatas={canGetDatas}
      gotData={gotData}
      comboboxQuery={comboboxQuery}
      setComboboxQuery={setComboboxQuery}
      udfComboboxQuery={udfComboboxQuery}
      setUdfComboboxQuery={setUdfComboboxQuery}
      comboboxOpen={comboboxOpen}
      setComboboxOpen={setComboboxOpen}
      isComboboxOpen={isComboboxOpen}
      setIsComboboxOpen={setIsComboboxOpen}
      showLongRunningDataLoadNotice={showLongRunningDataLoadNotice}
      isSaveConfirmationOpen={isSaveConfirmationOpen}
      setIsSaveConfirmationOpen={setIsSaveConfirmationOpen}
      saveCompleted={saveCompleted}
      setSaveCompleted={setSaveCompleted}
      isLoading={isLoading}
      canCancel={canCancel}
      error={error}
      setError={setError}
      percent={percent}
      isRefreshQueriesMessageShown={isRefreshQueriesMessageShown}
      setIsRefreshQueriesMessageShown={setIsRefreshQueriesMessageShown}
      enableDrilldownLinks={enableDrilldownLinks}
      setEnableDrilldownLinks={setEnableDrilldownLinks}
      setSelectedItem={setSelectedItem}
      setSelectedDataDefinitionIndex={setSelectedDataDefinitionIndex}
      loadDependentDefData={loadDependentDefData}
      handleOptionSelectLocal={handleOptionSelectLocal}
      handleUdfCategoryToggle={handleUdfCategoryToggle}
      handleUdfItemToggle={handleUdfItemToggle}
      handleMasterUdfToggle={handleMasterUdfToggle}
      handleGetDataLocal={handleGetDataLocal}
      handleGetData={handleGetData}
      handleCancelDataLoad={handleCancelDataLoad}
      handleSave={handleSave}
      handleSaveConfirmationClick={handleSaveConfirmationClick}
      setReportName={setReportName}
      setOverWrite={setOverWrite}
    />
  );
};

export default ReportParams;
